import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";

export interface MeUser {
  id: string;
  email: string;
  displayName: string;
}

export interface MeOrganization {
  id: string;
  name: string;
  slug: string;
  role: string;
  kind: "personal" | "group";
  personalOwnerUserId: string | null;
}

type AuthContextValue = {
  user: MeUser | null;
  organizations: MeOrganization[];
  activeOrganization: MeOrganization | null;
  personalOrganization: MeOrganization | null;
  loading: boolean;
  setActiveOrganizationId: (id: string) => void;
  refresh: () => Promise<void>;
  register: (input: { email: string; displayName: string; organizationName?: string; inviteToken?: string; claimToken?: string }) => Promise<void>;
  signIn: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeUser | null>(null);
  const [organizations, setOrganizations] = useState<MeOrganization[]>([]);
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(() => localStorage.getItem("lg.organization"));
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const data = await api<{ user: MeUser | null; organizations: MeOrganization[] }>("/api/auth/me");
    setUser(data.user);
    setOrganizations(data.organizations);
    setActiveOrganizationIdState((current) => {
      const next = data.organizations.some((organization) => organization.id === current) ? current : data.organizations[0]?.id ?? null;
      if (next) localStorage.setItem("lg.organization", next);
      return next;
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh().catch(() => setLoading(false));
  }, [refresh]);

  const setActiveOrganizationId = useCallback((id: string) => {
    localStorage.setItem("lg.organization", id);
    setActiveOrganizationIdState(id);
  }, []);

  const register = useCallback(
    async (input: { email: string; displayName: string; organizationName?: string; inviteToken?: string; claimToken?: string }) => {
      const challenge = await api<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>("/api/auth/register/options", {
        method: "POST",
        body: JSON.stringify(input),
      });
      const response = await startRegistration({ optionsJSON: challenge.options });
      await api("/api/auth/register/verify", { method: "POST", body: JSON.stringify({ challengeId: challenge.challengeId, response }) });
      await refresh();
    },
    [refresh],
  );

  const signIn = useCallback(
    async (email: string) => {
      const challenge = await api<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>("/api/auth/authenticate/options", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const response = await startAuthentication({ optionsJSON: challenge.options });
      await api("/api/auth/authenticate/verify", { method: "POST", body: JSON.stringify({ challengeId: challenge.challengeId, response }) });
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
    setOrganizations([]);
  }, []);

  const activeOrganization = organizations.find((organization) => organization.id === activeOrganizationId) ?? organizations[0] ?? null;
  const personalOrganization = organizations.find((organization) => organization.kind === "personal") ?? null;
  const value = useMemo(
    () => ({ user, organizations, activeOrganization, personalOrganization, loading, setActiveOrganizationId, refresh, register, signIn, signOut }),
    [user, organizations, activeOrganization, personalOrganization, loading, setActiveOrganizationId, refresh, register, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
